/**
 * Virtual-folder store. Lives at module scope so a folder the user
 * creates survives the AuiFiles instance being remounted (e.g. tree ↔
 * list view toggle) and is shared across sibling AuiFiles instances
 * pointing at the same owner.
 *
 * Both adapters use this:
 * - VFS adapter has no server-side folder model, so the set IS the
 *   folder list.
 * - KB adapter calls `kbApi.mkdir` for persistence AND mirrors the
 *   path here so the new folder shows up immediately, without having
 *   to wait on (or trust) a refetch of `listNodes(recursive: true)`
 *   to surface the freshly-created row.
 *
 * A virtual entry is removed automatically as soon as a real file
 * appears inside it — at that point the path is realised on the
 * server and the local entry would only shadow.
 */

const virtualFoldersByOwner = new Map<string, Set<string>>();
const folderListeners = new Map<string, Set<() => void>>();

export function normaliseFolderPath(path: string): string {
  return path.replace(/^\/+|\/+$/g, '');
}

export function getVirtualFolders(ownerId: string): Set<string> {
  let s = virtualFoldersByOwner.get(ownerId);
  if (!s) {
    s = new Set();
    virtualFoldersByOwner.set(ownerId, s);
  }
  return s;
}

function notifyFolderListeners(ownerId: string): void {
  const listeners = folderListeners.get(ownerId);
  if (!listeners) return;
  for (const fn of listeners) fn();
}

export function subscribeVirtualFolders(
  ownerId: string,
  fn: () => void,
): () => void {
  let listeners = folderListeners.get(ownerId);
  if (!listeners) {
    listeners = new Set();
    folderListeners.set(ownerId, listeners);
  }
  listeners.add(fn);
  return () => {
    listeners!.delete(fn);
  };
}

export function addVirtualFolder(ownerId: string, path: string): void {
  const norm = normaliseFolderPath(path);
  if (!norm) return;
  const set = getVirtualFolders(ownerId);
  if (set.has(norm)) return;
  set.add(norm);
  notifyFolderListeners(ownerId);
}

/** When a real file lands at `filePath`, drop any virtual folder the
 *  file now lives inside — the path is real on the server now. */
export function pruneRealisedFolders(ownerId: string, filePath: string): void {
  const norm = normaliseFolderPath(filePath);
  if (!norm) return;
  const set = getVirtualFolders(ownerId);
  if (set.size === 0) return;
  let changed = false;
  for (const folder of set) {
    if (norm === folder || norm.startsWith(folder + '/')) {
      set.delete(folder);
      changed = true;
    }
  }
  if (changed) notifyFolderListeners(ownerId);
}
